import axios from 'axios';

const getCsrfToken = () => {
  const name = 'csrftoken';
  const cookieValue = document.cookie.split('; ').find(row => row.startsWith(name))?.split('=')[1];
  return cookieValue;
};

// Base URL relativa: el frontend siempre llama al backend del mismo origen.
// En desarrollo, el proxy de package.json redirige /api al servidor de Django.
// En producción, frontend y backend se sirven bajo el mismo dominio.
const API_BASE_URL = '/api';

// Funciones para el usuario

export const registerUser = async (username, email, full_name, password) => {
  const payload = {
    username,
    email,
    full_name,
    password,
  };

  const response = await axios.post(`${API_BASE_URL}/users/register`, payload, {
    headers: {
      'X-CSRFToken': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });
  return response.data;
};

export const loginUser = async (username, password) => {
  const response = await axios.post(`${API_BASE_URL}/users/login`, {
    username,
    password,
  }, {
    headers: {
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const getUserDetails = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/users/info`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const getUserProfile = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/users/profile`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const updateUserProfile = async (token, formData) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/users/profile`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-CSRFToken': getCsrfToken(),
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendPasswordResetEmail = async (email) => {
  const response = await axios.post(`${API_BASE_URL}/users/reset-password`, {
    email,
  }, {
    headers: {
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const resetPassword = async (uidb64, token, newPassword) => {
  const response = await axios.post(`${API_BASE_URL}/users/reset-password/confirm`, {
    uidb64,
    token,
    new_password: newPassword,
  }, {
    headers: {
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

// apiServices.js

export const changePassword = async (token, currentPassword, newPassword) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/users/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-CSRFToken': getCsrfToken(),
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    // Manejar el error
    throw error;
  }
};

export const getCompanyStatus = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/companies/status`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const getMyCompany = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/companies/my-company`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

export const createCompany = async (token, companyData) => {
  const response = await axios.post(`${API_BASE_URL}/companies/create`, companyData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });
  return response.data;
};

export const updateCompany = async (token, companyData) => {
  const response = await axios.patch(`${API_BASE_URL}/companies/my-company`, companyData, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });
  return response.data;
};

export const getCompanyList = async (token, params = {}) => {
  const response = await axios.get(`${API_BASE_URL}/companies/list`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
    params: params // axios se encarga de convertir { page: 1 } en ?page=1
  });
  return response.data;
};

/**
 * Obtiene recomendaciones de empresas personalizadas
 */
export const getCompanyRecommendations = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/companies/recommendations`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Obtiene los detalles de una empresa específica por su ID.
 */
export const getCompanyById = async (token, companyId) => {
  const response = await axios.get(`${API_BASE_URL}/companies/${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Crea una nueva solicitud de conexión.
 */
export const createConnection = async (token, toCompanyId, message) => {
  const response = await axios.post(`${API_BASE_URL}/connections/create`,
    { to_company: toCompanyId, message: message },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-CSRFToken': getCsrfToken(),
        'Content-Type': 'application/json',
      },
      withCredentials: true,
  });
  return response.data;
};

/**
 * Obtiene las solicitudes enviadas por el usuario.
 */
export const getSentConnections = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/connections/sent`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Obtiene las solicitudes recibidas por el usuario.
 */
export const getReceivedConnections = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/connections/received`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Responde a una solicitud de conexión (Aceptar o Rechazar).
 * 'action' debe ser 'accept' o 'reject'.
 */
export const respondToConnection = async (token, connectionId, action) => {
  const response = await axios.post(`${API_BASE_URL}/connections/${connectionId}/respond`,
    { action: action },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-CSRFToken': getCsrfToken(),
        'Content-Type': 'application/json',
      },
      withCredentials: true,
  });
  return response.data;
};

/**
 * Obtiene estadísticas de conexiones (Aceptadas, Pendientes, Rechazadas).
 */
export const getConnectionStats = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/connections/stats`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Obtiene las conexiones activas (aceptadas) del usuario.
 */
export const getActiveConnections = async (token) => {
  const response = await axios.get(`${API_BASE_URL}/connections/active`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};

/**
 * Cancela una solicitud de conexión enviada pendiente.
 */
export const cancelConnection = async (token, connectionId) => {
  const response = await axios.delete(`${API_BASE_URL}/connections/${connectionId}/cancel`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRFToken': getCsrfToken(),
    },
    withCredentials: true,
  });
  return response.data;
};
